import React from "react";
import { Bath, BedDouble, ImageOff, MapPin, Square } from "lucide-react";

/**
 * The listing as a card in a list.
 *
 * Presentation only: it renders the model `buildListingPreviewModel` derives
 * and nothing else. No form control, no submit, no `name` attribute — this
 * card lives inside the editor's single form, so anything focusable and named
 * in here would silently join the payload.
 *
 * It shows the eight fields the public card shows. The publication flag, the
 * availability and the marketing v2 fields are deliberately absent: they are
 * admin metadata and belong to the status block beside this card, not inside
 * a thing that claims to be a website listing.
 */

/** Nothing filled in yet — stated, never faked with a zero or a dash. */
function Missing({ children }) {
  return <span className="italic text-muted-foreground">{children}</span>;
}

function Meta({ icon: Icon, value, unit, label }) {
  return (
    <div className="flex items-center gap-1.5" title={label}>
      <Icon aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
      <span className="text-[13px] text-foreground">
        {value} {unit}
      </span>
    </div>
  );
}

export default function ListingPreviewCard({ model }) {
  const meta = [
    model.bedrooms !== null && {
      key: "bedrooms",
      icon: BedDouble,
      value: model.bedrooms,
      unit: "Schlafzimmer",
      label: "Schlafzimmer",
    },
    model.bathrooms !== null && {
      key: "bathrooms",
      icon: Bath,
      value: model.bathrooms,
      unit: "Badezimmer",
      label: "Badezimmer",
    },
    model.sizeSqm !== null && {
      key: "size",
      icon: Square,
      value: model.sizeSqm,
      unit: "m²",
      label: "Fläche",
    },
  ].filter(Boolean);

  return (
    <article
      aria-label="Inseratsvorschau"
      className="w-full max-w-[420px] overflow-hidden rounded-xl border border-border bg-card"
    >
      <div className="relative aspect-[4/3] w-full bg-muted">
        {model.imageUrl ? (
          <img
            src={model.imageUrl}
            /* Named, not decorative: this is the listing's own photo and the
               panel exists to judge how it reads. The table's thumbnail names
               itself the same way. */
            alt="Vorschaubild des Inserats"
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            data-testid="listing-preview-image-placeholder"
            className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground"
          >
            <ImageOff aria-hidden="true" className="h-7 w-7" />
            <span className="text-[12px]">Noch kein Bild hochgeladen</span>
          </div>
        )}

        <div className="absolute right-3 top-3 rounded-lg bg-card/95 px-2.5 py-1.5 shadow-sm backdrop-blur-sm">
          <p className="m-0 text-[13px] font-semibold text-foreground">
            {model.priceLabel ? (
              <>CHF {model.priceLabel}</>
            ) : (
              <Missing>Preis offen</Missing>
            )}
          </p>
          <p className="m-0 text-[11px] text-muted-foreground">pro Monat</p>
        </div>
      </div>

      <div className="p-4">
        <p className="m-0 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
          <MapPin aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 break-words">
            {model.location || <Missing>Ort nicht angegeben</Missing>}
          </span>
        </p>

        <h6 className="m-0 mt-1.5 break-words text-[15px] font-semibold leading-snug text-foreground">
          {model.title || <Missing>Titel fehlt</Missing>}
        </h6>

        <p className="m-0 mt-2 line-clamp-3 break-words text-[13px] leading-relaxed text-muted-foreground">
          {model.description || <Missing>Noch keine Beschreibung</Missing>}
        </p>

        {meta.length > 0 ? (
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-3">
            {meta.map((m) => (
              <Meta
                key={m.key}
                icon={m.icon}
                value={m.value}
                unit={m.unit}
                label={m.label}
              />
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}
